<template>
  <div 
    class="group relative p-2 rounded-lg transition-all duration-200 border-[1px] border-transparent hover:border-primary/20 cursor-pointer"
    :class="[
      message.is_read 
        ? 'bg-background/50' 
        : 'bg-background shadow-sm'
    ]"
    @click="handleMessageClick"
  >
    <!-- Priority indicator -->
    <div 
      v-if="message.priority === 'urgent' || message.priority === 'high'"
      class="absolute left-1 top-1 bottom-1 w-1 rounded-full"
      :class="message.priority === 'urgent' ? 'bg-red-500' : 'bg-orange-500'"
    />

    <!-- Message content -->
    <div class="flex items-start gap-2">
      <!-- Icon -->
      <div class="flex-shrink-0 -mt-1 pl-2">
        <Icon 
          :name="getMessageIcon(message.message_type)" 
          class="w-4 h-4 relative -top-0.5"
          :class="[
            
            !message.is_read ? 'opacity-100' : 'opacity-60'
          ]"
        />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-2">
          <h4 
            class="text-xs font-medium leading-tight truncate"
            :class="!message.is_read ? 'text-foreground' : 'text-muted-foreground'"
          >
            {{ message.title }}
          </h4>
          
          <!-- Archive button -->
          <Button 
            variant="ghost" 
            size="sm"
            class="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 transition-opacity"
            @click.stop="handleArchiveClick"
          >
            <Icon 
              :name="message.is_archived ? 'lucide:archive-restore' : 'solar:archive-down-bold'" 
              class="w-3 h-3" 
            />
          </Button>
        </div>
        <p 
          class="text-xs leading-relaxed mt-1 line-clamp-2"
          :class="!message.is_read ? 'text-foreground/80' : 'text-muted-foreground/70'"
        >
          {{ message.content }}
        </p>

        <!-- Footer -->
        <div class="flex items-center justify-between mt-2">
          <span class="text-[10px] text-muted-foreground">
            {{ formatTimeAgo(message.created_at) }}
          </span>

          <!-- Action button if available -->
          <Button 
            v-if="message.action_url && message.action_label"
            size="sm"
            class="h-5 px-2 text-[10px] rounded-xl bg-primary text-background"
            @click.stop="handleActionClick"
          >
            {{ message.action_label }}
          </Button>

          <!-- Read indicator -->
          <div 
            v-else-if="!message.is_read"
            class="w-2 h-2 bg-primary rounded-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import type { UserMessage } from '@/stores/messageStore'
import { useMessages } from '@/composables/useMessages'

interface Props {
  message: UserMessage
}

interface Emits {
  markAsRead: [messageId: string]
  archive: [messageId: string]
  unarchive: [messageId: string]
  messageClick: [message: UserMessage]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { getMessageIcon, getPriorityColor, formatTimeAgo } = useMessages()

const handleMessageClick = async () => {
  // Emit the message click event to parent
  emit('messageClick', props.message)
}

const handleActionClick = () => {
  if (props.message.action_url) {
    window.open(props.message.action_url, '_blank')
  }
}

const handleArchiveClick = () => {
  if (props.message.is_archived) {
    emit('unarchive', props.message.id)
  } else {
    emit('archive', props.message.id)
  }
}

</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style> 